import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody } from "@material-tailwind/react";
import Chart from "react-apexcharts";
import {
  getQuarterlyCommission,
  getOneTimeRateDetails,
  getProfileDetailsForCommission
} from "@/store/slices/userSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { chartsConfig } from "@/configs";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

function formatCurrency(amount) {
  if (amount == null || isNaN(amount)) return "0";
  return amount;
  // const num = Number(amount);
  // return num % 1 === 0
  //   ? num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  //   : num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function PerformanceDashboard({ user_id }) {
  const { t } = useTranslation("internal-user");
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  // Extract state from Redux
  const { commissionData, onTimeRates, userCommission, loading } = useSelector((state) => state.user);

  // Single Global Filter State (Defaults to 1 Month to mimic the original "This Month" view)
  const [globalMonths, setGlobalMonths] = useState(1);
  const userId = user_id ?? user?.id;

  // Fetch ALL data whenever the global filter changes
  useEffect(() => {
    if (userId) {
      dispatch(getQuarterlyCommission({ id: userId, months: globalMonths }));
      dispatch(getOneTimeRateDetails({ id: userId, months: globalMonths }));
      dispatch(getProfileDetailsForCommission({ id: userId, months: globalMonths }));
    }
  }, [dispatch, globalMonths, userId]);

  // Safely extract nested JSON Data
  const profitDetails = userCommission || {};
  const onTimeData = onTimeRates?.data?.on_time_rate || onTimeRates?.on_time_rate || {};
  const quarterlyData = commissionData?.data || commissionData || {};

  // Profit Details Mapping
  const salesThisMonth = profitDetails.sales_this_month || {};
  const targetAchieved = profitDetails.target_achieved || {};
  const dealClosed = profitDetails.deal_closed || {};
  const commissionEarned = profitDetails.commission_earned || {};

  // On-Time Rate Mapping
  const onTimePercentage = onTimeData.percentage ?? profitDetails.on_time_rate?.percentage ?? 0;
  const onTimeDeliveries = onTimeData.on_time ?? 0;
  const totalDeliveries = onTimeData.total ?? 0;
  const onTimeComparison = onTimeData.comparison_last_month || {};
  const bestMonth = onTimeData.best_month || {};

  // Commission Chart Mapping
  const totalCommission = quarterlyData.total_commission ?? commissionEarned.value ?? 0;
  const commissionChange = quarterlyData.percentage_change || {};
  const quarterlyBreakdown = quarterlyData.quarterly_breakdown || [];

  const chartCategories = quarterlyBreakdown.map((q) =>
    q.year != null && q.quarter ? `${q.year} ${q.quarter}` : q.quarter || "—"
  );
  const chartSeries = quarterlyBreakdown.map((q) => Number(q.amount) || 0);

  // Exact UI Colors
  const blueColor = "#52B6EA";
  const greenColor = "#27AE60";
  const grayTrackColor = "#788390";

  const commissionChartOptions = {
    ...chartsConfig,
    chart: { ...chartsConfig.chart, type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "55%", borderRadius: 2 } },
    colors: [blueColor],
    xaxis: { ...chartsConfig.xaxis, categories: chartCategories },
    yaxis: {
      ...chartsConfig.yaxis,
      labels: { ...chartsConfig.yaxis?.labels, formatter: (val) => formatCurrency(val) },
    },
    dataLabels: { enabled: false },
    grid: { show: true, borderColor: "#f3f4f6", strokeDashArray: 0, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
    tooltip: { ...chartsConfig.tooltip, y: { formatter: (val) => formatCurrency(val) } },
  };

  const radialChartOptions = {
    chart: { type: "radialBar", offsetY: -10, sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: { size: "65%" },
        track: { background: grayTrackColor },
        dataLabels: {
          name: { show: true, fontSize: "13px", fontWeight: 400, color: "#4b5563", offsetY: 25 },
          value: {
            show: true,
            fontSize: "22px",
            fontWeight: 700,
            color: "#111827",
            offsetY: -10,
            formatter: (val) => `${val}%`,
          },
        },
      },
    },
    colors: [blueColor],
    labels: [t('on_time_rate')],
  };

  const radialSeries = [Number(onTimePercentage) || 0];

  const bestMonthLabel = bestMonth?.month
    ? `${bestMonth.month} (${bestMonth.on_time_deliveries} ${t('deliveries')})`
    : "—";

  const renderComparison = (change, prefix = t('vs_prev_period')) => {
    if (!change || Object.keys(change).length === 0) {
      return <span className="text-sm text-gray-400 font-medium mt-3 block">—</span>;
    }

    const val = change?.value ?? change?.difference ?? change?.percentage ?? 0;
    const isUp = change?.is_increase ?? val >= 0;

    return (
      <span className={`text-sm font-medium flex items-center gap-1 mt-3 ${isUp ? "text-[#27AE60]" : "text-red-500"}`}>
        <span className="text-[10px]">{isUp ? "▲" : "▼"}</span>
        {val}% {prefix}
      </span>
    );
  };

  // Helper text for dynamic timeframe labeling
  const timeLabel = globalMonths === 1 ? t('this_month') : `(Last ${globalMonths} Months)`;

  return (
    <div className="mt-5 mb-8 flex flex-col gap-6 font-sans">
      <ScrollToTop />

      <div className="max-w-7xl mx-auto w-full">
        
        {/* Global Filter Header */}
        <div className="flex justify-between items-end mb-6">
          <div>
            {/* <h2 className="text-2xl font-bold text-gray-900">Performance Overview</h2>
            <p className="text-sm text-gray-500">Track sales, conversions, and commissions.</p> */}
          </div>
          
          {/* Global Dropdown */}
        <div className="relative w-48 sm:w-56">
          {/* Absolute Icon Container */}
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FunnelIcon className="h-4 w-4 text-gray-500" strokeWidth={2.5} />
          </div>
          
          {/* Select Input with left padding (pl-9) to make room for the icon */}
          <select
            value={globalMonths}
            onChange={(e) => setGlobalMonths(Number(e.target.value))}
            className="block w-full pl-9 pr-8 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#52B6EA] focus:border-transparent cursor-pointer transition-all"
          >
            <option value={1}>{t('this_month')}</option>
            <option value={3}>{t('last_3_months')}</option>
            <option value={6}>{t('last_6_months')}</option>
            <option value={12}>{t('last_12_months')}</option>
          </select>
        </div>
        </div>

        {loading && (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin" />
          </div>
        )}

        {!loading && (
          <>
            {/* 5 Cards Row - Real Time Mapping */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">

              {/* Sales */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CardBody className="p-5">
                  <div className="text-[18px] text-gray-900 font-bold">
                    {formatCurrency(salesThisMonth.value || 0)}
                  </div>
                  {/* <div className="text-sm text-gray-600 mt-4">Sales {timeLabel} (SAR)</div> */}
                  <div className="text-sm text-gray-600 mt-4">{t('sales')} (SAR)</div>
                  {renderComparison({ value: salesThisMonth.percentage, is_increase: salesThisMonth.is_increase }, t('vs_prev_period'))}
                </CardBody>
              </Card>

              {/* Target Achieved */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CardBody className="p-5">
                  <div className="text-[18px] text-gray-900 font-bold">
                    {targetAchieved.percentage || 0}%
                  </div>
                  <div className="text-sm text-gray-600 mt-4">{t('target_achieved')}</div>
                  <span className={`text-sm font-medium mt-3 block ${(targetAchieved.percentage || 0) >= 80 ? "text-[#27AE60]" : "text-amber-500"}`}>
                    {(targetAchieved.percentage || 0) >= 80
                      ? t('above_average')
                      : `${targetAchieved.converted_to_order || 0}/${targetAchieved.total_assigned || 0} ${t('converted')}`}
                  </span>
                </CardBody>
              </Card>

              {/* Deal Closed */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CardBody className="p-5">
                  <div className="text-[18px] text-gray-900 font-bold">
                    {dealClosed.value || 0}
                  </div>
                  <div className="text-sm text-gray-600 mt-4">{t('deals_closed')}</div>
                  {renderComparison({ value: dealClosed.percentage, is_increase: dealClosed.is_increase }, t('vs_prev_period'))}
                </CardBody>
              </Card>

              {/* On-Time Rate */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CardBody className="p-5">
                  <div className="text-[18px] text-gray-900 font-bold">
                    {Number(profitDetails.on_time_rate?.percentage || 0).toFixed(0)}%
                  </div>
                  <div className="text-sm text-gray-600 mt-4">{t('on_time_rate')}</div>
                  <span className={`text-sm font-medium mt-3 block ${(profitDetails.on_time_rate?.percentage || 0) >= 90 ? "text-[#27AE60]" : "text-gray-500"}`}>
                    {(profitDetails.on_time_rate?.percentage || 0) >= 90
                      ? t('best_in_office')
                      : `${profitDetails.on_time_rate?.on_time || 0}/${profitDetails.on_time_rate?.total || 0} ${t('deliveries')}`}
                  </span>
                </CardBody>
              </Card>

              {/* Commission Earned */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CardBody className="p-5">
                  <div className="text-[18px] text-gray-900 font-bold">
                    {formatCurrency(commissionEarned.value || 0)}
                  </div>
                  <div className="text-sm text-gray-600 mt-4">{t('commission_earned')}</div>
                  {renderComparison({ value: commissionEarned.percentage, is_increase: commissionEarned.is_increase }, t('vs_prev_period'))}
                </CardBody>
              </Card>
            </div>

            {/* Bottom Row - Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Quarterly Commission Bar Chart Card */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden relative">
                <CardBody className="p-6">
                  <div className="flex flex-col items-center justify-center text-center mt-2 mb-8">
                    <div className="text-[18px] text-gray-900 font-bold">
                      {formatCurrency(totalCommission)}
                    </div>
                    <div className="text-sm text-gray-800 mt-1">{t('commission_earned_sar')}</div>
                    <div className="flex justify-center">
                      {renderComparison(commissionChange, t('vs_prev_period'))}
                    </div>
                  </div>

                  <div className="w-full overflow-x-auto">
                    {chartSeries.length > 0 && chartSeries.some((v) => v > 0) ? (
                      <Chart
                        key={`commission-chart-${globalMonths}`}
                        type="bar"
                        height={260}
                        series={[{ name: t('commission'), data: chartSeries }]}
                        options={commissionChartOptions}
                      />
                    ) : (
                      <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">
                        {t('no_commission_data')}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* On-Time Rate Radial Chart Card */}
              <Card className="rounded-xl shadow-sm border border-gray-100 overflow-hidden relative">
                <CardBody className="p-6 flex flex-col h-full">
                  <div className="flex flex-col items-center justify-center text-center mt-4">
                    <div className="text-[16px] text-gray-900 font-bold">{t('on_time_rate')}</div>
                    <div className="flex justify-center">
                      {renderComparison(onTimeComparison, t('vs_prev_period'))}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col justify-center items-center mt-8">
                    <div className="w-full flex justify-center -mb-6">
                      <Chart
                        key={`radial-chart-${globalMonths}`}
                        type="radialBar"
                        height={280}
                        series={radialSeries}
                        options={radialChartOptions}
                      />
                    </div>

                    <div className="w-full px-2 mt-4">
                      <div className="h-[1px] w-full bg-gray-200 mb-4"></div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-[13px] text-gray-700">{t('on_time_deliveries')}</span>
                        <span className="text-[13px] text-gray-900 font-bold">
                          {totalDeliveries > 0 ? `${onTimeDeliveries}/${totalDeliveries}` : "0/0"}
                        </span>
                      </div>
                      <div className="h-[1px] w-full bg-gray-200 my-2"></div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-[13px] text-gray-700">{t('best_month')}</span>
                        <span className="text-[13px] text-gray-900 font-bold">
                          {bestMonthLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>

            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PerformanceDashboard;